import React from "react";
import {
  CheckCircle,
  Edit,
  ArrowUpRight,
  Calendar,
  Clock,
  Briefcase,
  User,
  Users,
  FileText,
  MapPin,
  Wrench,
  Tag,
  Hash,
} from "lucide-react";

// Icon mapping for dynamic rendering
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  CheckCircle,
  Edit,
  Calendar,
  Clock,
  Briefcase,
  User,
  Users,
  FileText,
  MapPin,
  Wrench,
  Tag,
  Hash,
};

// Formatter functions mapping
const formatters: Record<string, (value: any) => string> = {
  hours: (value: any) => `${value}h`,
  date: (value: any) => {
    try {
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      if (dateOnly.getTime() === today.getTime()) return "Today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (dateOnly.getTime() === yesterday.getTime()) return "Yesterday";

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (dateOnly.getTime() === tomorrow.getTime()) return "Tomorrow";

      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(value);
    }
  },
};

interface FieldMetadata {
  displayKey: string;
  label: string;
  icon: string;
  colorClass: {
    light: string;
    dark: string;
  };
  format?: string;
  type: "text" | "number" | "date" | "entity" | "duration";
}

interface EntityConfirmationCardProps {
  message: string;
  parameters: Record<string, any>;
  fieldMetadata?: FieldMetadata[];
  actionType: string;
  onConfirm: () => void;
  onEdit: (field: string) => void;
  onManual: () => void;
  isDark?: boolean;
  availableActions?: Array<{
    type: "manual_action";
    action: string;
    label: string;
    icon: string;
  }>;
  showActions?: boolean; // Whether to show action buttons (default: true)
}

export const EntityConfirmationCard: React.FC<EntityConfirmationCardProps> = ({
  message,
  parameters,
  fieldMetadata = [],
  actionType,
  onConfirm,
  onEdit,
  onManual,
  isDark = false,
  availableActions = [],
  showActions = true,
}) => {
  // Get icon component
  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || CheckCircle;
    return <IconComponent className="w-4 h-4" />;
  };

  // Get button text based on action type
  const getConfirmButtonText = (): string => {
    if (actionType?.startsWith("delete_")) {
      return "Confirm & Delete";
    } else if (actionType?.startsWith("edit_") || actionType?.startsWith("update_")) {
      return "Confirm & Update";
    } else if (actionType?.startsWith("create_")) {
      return "Confirm & Create";
    }
    // Default fallback
    return "Confirm";
  };

  // Get manual action button text based on action type
  const getManualButtonText = (): string => {
    if (actionType?.startsWith("delete_")) {
      return "Cancel";
    } else if (actionType?.startsWith("edit_") || actionType?.startsWith("update_")) {
      return "Cancel";
    } else if (actionType?.startsWith("create_")) {
      return "Create Manually";
    }
    return "Cancel";
  };

  // Get header title based on action type
  const getHeaderTitle = (): string => {
    if (!showActions) {
      return "Created Successfully";
    }
    if (actionType?.startsWith("delete_")) {
      return "Confirm Deletion";
    } else if (actionType?.startsWith("edit_") || actionType?.startsWith("update_")) {
      return "Confirm Changes";
    } else if (actionType?.startsWith("create_")) {
      return "Confirm Details";
    }
    return "Confirm Details";
  };

  // Format value based on field metadata
  const formatValue = (value: any, metadata?: FieldMetadata): string => {
    if (value === null || value === undefined || value === "") {
      return "Not provided";
    }

    if (metadata?.format && formatters[metadata.format]) {
      return formatters[metadata.format](value);
    }

    if (Array.isArray(value)) {
      return `${value.length} selected`;
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Render field value
  const renderField = (metadata: FieldMetadata) => {
    const value =
      parameters[metadata.displayKey] ||
      parameters[metadata.displayKey.toLowerCase()];
    const displayValue = formatValue(value, metadata);

    return (
      <div
        key={metadata.displayKey}
        className={`flex items-center justify-between p-3 rounded-lg border ${
          isDark ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex items-center gap-3 flex-1">
          <div
            className={`p-2 rounded ${
              isDark ? metadata.colorClass.dark : metadata.colorClass.light
            }`}
          >
            {getIcon(metadata.icon)}
          </div>
          <div className="flex-1">
            <div
              className={`text-xs font-medium ${
                isDark ? "text-gray-500" : "text-gray-600"
              }`}
            >
              {metadata.label}
            </div>
            <div
              className={`text-sm font-medium mt-0.5 ${
                isDark ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {displayValue}
            </div>
          </div>
        </div>
        {showActions && (
          <button
            onClick={() => onEdit(metadata.displayKey)}
            className={`ml-2 p-1.5 rounded transition-colors ${
              isDark
                ? "hover:bg-gray-700 text-gray-500 hover:text-gray-300"
                : "hover:bg-gray-200 text-gray-500 hover:text-gray-700"
            }`}
            title="Edit this field"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={`relative rounded-lg border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      } p-4 space-y-4`}
    >
      {/* Manual action button - Top right corner */}
      {availableActions.length > 0 && (
        <button
          onClick={onManual}
          className={`
            absolute -top-2 -right-2 
            p-2 rounded-lg shadow-lg
            transition-all duration-200
            hover:scale-110
            z-10
            ${
              isDark
                ? "bg-accent-600 hover:bg-accent-700 text-white"
                : "bg-accent-500 hover:bg-accent-600 text-white"
            }
          `}
          title={availableActions[0]?.label || "Create manually"}
        >
          <ArrowUpRight className="w-4 h-4" />
        </button>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 p-2 rounded ${
            showActions
              ? actionType?.startsWith("delete_")
                ? isDark
                  ? "bg-error-600 text-error-200"
                  : "bg-error-100 text-error-600"
                : isDark
                  ? "bg-blue-600 text-blue-200"
                  : "bg-accent-100 text-accent-600"
              : isDark
                ? "bg-success-600 text-success-200"
                : "bg-success-100 text-success-600"
          }`}
        >
          <CheckCircle className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h4
            className={`font-semibold text-sm mb-1 ${
              showActions
                ? actionType?.startsWith("delete_")
                  ? isDark
                    ? "text-error-400"
                    : "text-error-700"
                  : isDark
                    ? "text-gray-200"
                    : "text-gray-900"
                : isDark
                  ? "text-success-400"
                  : "text-gray-900"
            }`}
          >
            {getHeaderTitle()}
          </h4>
          <p
            className={`text-sm ${
              showActions
                ? actionType?.startsWith("delete_")
                  ? isDark
                    ? "text-error-300"
                    : "text-error-700"
                  : isDark
                    ? "text-gray-400"
                    : "text-gray-700"
                : isDark
                  ? "text-success-300"
                  : "text-gray-700"
            }`}
          >
            {message}
          </p>
        </div>
      </div>

      {/* Fields */}
      {fieldMetadata.length > 0 ? (
        <div className="space-y-2">
          {fieldMetadata.map((metadata) => renderField(metadata))}
        </div>
      ) : (
        <div
          className={`text-sm p-3 rounded ${
            isDark ? "bg-gray-900 text-gray-500" : "bg-gray-50 text-gray-600"
          }`}
        >
          No details to display
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex gap-2 pt-2">
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
              actionType?.startsWith("delete_")
                ? isDark
                  ? "bg-error-600 hover:bg-error-700 text-white"
                  : "bg-error-600 hover:bg-error-700 text-white"
                : isDark
                  ? "bg-accent-600 hover:bg-accent-700 text-white"
                  : "bg-accent-600 hover:bg-accent-700 text-white"
            }`}
          >
            {getConfirmButtonText()}
          </button>
          {availableActions.length > 0 && (
            <button
              onClick={onManual}
              className={`py-2.5 px-4 rounded-lg font-medium text-sm transition-colors border ${
                isDark
                  ? "border-gray-700 hover:bg-gray-700 text-gray-300"
                  : "border-gray-300 hover:bg-gray-50 text-gray-700"
              }`}
            >
              {getManualButtonText()}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
